import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '@/components/AppHeader';
import { getAllMatches, deleteMatchHard, deleteMatchesByStatus, deleteAllMatchesHard, getOrphanMatches, assignMatchToTournament, discardMatch, getTournaments, resetCurrentSet } from '@/lib/supabase';
import { getTeams } from '@/lib/supabase';
import type { Team, Tournament } from '@/types';
import { useAuth } from '@/lib/auth';

interface MatchRow {
  id: string;
  matchNumber: number;
  broadcastId: string;
  status: string;
  date: string | null;
  matchType: string | null;
  teamAName: string;
  teamBName: string;
  tournamentId: string | null;
  tournamentName: string | null;
  createdAt: string;
}

interface Stats {
  total: number;
  live: number;
  completed: number;
  discarded: number;
  pending: number;
  other: number;
}

type BulkAction = 'completed' | 'discarded' | 'all' | null;

interface OrphanMatch {
  id: string;
  match_number: number;
  date: string | null;
  match_type: string | null;
  team_a_id: string;
  team_b_id: string;
  status: string;
  created_at: string;
}

interface AdminMatchManagementProps {
  onSignOut: () => void;
}

export default function AdminMatchManagement({ onSignOut }: AdminMatchManagementProps) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, live: 0, completed: 0, discarded: 0, pending: 0, other: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<BulkAction>(null);
  const [confirmText, setConfirmText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyBroadcastUrl = async (broadcastId: string) => {
    try {
      const baseUrl = import.meta.env.VITE_PUBLIC_BASE_URL || window.location.origin;
      const url = `${baseUrl}/overlay/${broadcastId}`;
      await navigator.clipboard.writeText(url);
      setCopiedId(broadcastId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Discard confirmation state
  const [discardConfirmId, setDiscardConfirmId] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);

  // Orphan match repair state
  const [orphans, setOrphans] = useState<OrphanMatch[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [showOrphanModal, setShowOrphanModal] = useState(false);
  const [selectedOrphan, setSelectedOrphan] = useState<OrphanMatch | null>(null);
  const [assignTournamentId, setAssignTournamentId] = useState<string>('');
  const [repairing, setRepairing] = useState(false);

  // Reset current set state
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [matchToReset, setMatchToReset] = useState<MatchRow | null>(null);
  const [resetReason, setResetReason] = useState('');
  const [resetting, setResetting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rawMatches, rawTeams, rawTournaments] = await Promise.all([getAllMatches(), getTeams(), getTournaments()]);
      const teams: Map<string, Team> = new Map(
        rawTeams.map((t: any) => [t.id, { id: t.id, name: t.name, shortName: t.short_name, color: t.color, logoUrl: t.logo_url, players: [] }])
      );
      const tournaments: Map<string, string> = new Map(
        rawTournaments.map((t: any) => [t.id, t.name])
      );

      const rows: MatchRow[] = rawMatches.map((m: any) => ({
        id: m.id,
        matchNumber: m.match_number,
        broadcastId: m.broadcast_id,
        status: m.status,
        date: m.date,
        matchType: m.match_type,
        teamAName: teams.get(m.team_a_id)?.shortName ?? '?',
        teamBName: teams.get(m.team_b_id)?.shortName ?? '?',
        tournamentId: m.tournament_id,
        tournamentName: m.tournament_id ? tournaments.get(m.tournament_id) ?? null : null,
        createdAt: m.created_at,
      }));

      setMatches(rows);
      setStats({
        total: rows.length,
        live: rows.filter(r => r.status === 'live' || r.status === 'set-won').length,
        completed: rows.filter(r => r.status === 'match-won').length,
        discarded: rows.filter(r => r.status === 'discarded').length,
        pending: rows.filter(r => r.status === 'setup' || r.status === 'first-server').length,
        other: rows.filter(r => !['live','set-won','match-won','discarded','setup','first-server'].includes(r.status)).length,
      });
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadOrphans = useCallback(async () => {
    try {
      const [orphanData, tournamentData, teamData] = await Promise.all([
        getOrphanMatches(),
        getTournaments(),
        getTeams(),
      ]);
      setOrphans(orphanData.map((m: any) => ({
        id: m.id,
        match_number: m.match_number,
        date: m.date,
        match_type: m.match_type,
        team_a_id: m.team_a_id,
        team_b_id: m.team_b_id,
        status: m.status,
        created_at: m.created_at,
      })));
      setTournaments(tournamentData);
      setTeams(teamData.map((t: any) => ({
        id: t.id,
        name: t.name,
        shortName: t.short_name,
        color: t.color,
        logoUrl: t.logo_url,
        players: [],
      })));
    } catch (err) {
      console.error('Failed to load orphans:', err);
    }
  }, []);

  useEffect(() => { loadOrphans(); }, [loadOrphans]);

  const handleAssignOrphan = async () => {
    if (!selectedOrphan || !assignTournamentId) return;
    setRepairing(true);
    try {
      await assignMatchToTournament(selectedOrphan.id, assignTournamentId);
      setShowOrphanModal(false);
      setSelectedOrphan(null);
      setAssignTournamentId('');
      await Promise.all([loadData(), loadOrphans()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign tournament');
    } finally {
      setRepairing(false);
    }
  };

  const handleDiscardOrphan = async () => {
    if (!selectedOrphan) return;
    setRepairing(true);
    try {
      await discardMatch(selectedOrphan.id);
      setShowOrphanModal(false);
      setSelectedOrphan(null);
      await Promise.all([loadData(), loadOrphans()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discard match');
    } finally {
      setRepairing(false);
    }
  };

  const handleDeleteOne = async (id: string) => {
    setDeleting(id);
    try {
      await deleteMatchHard(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  // Handle discard match (soft delete - sets status to 'discarded')
  const handleDiscardMatch = async (id: string) => {
    setDiscarding(true);
    try {
      await discardMatch(id);
      setDiscardConfirmId(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discard match');
    } finally {
      setDiscarding(false);
    }
  };

  // Handle reset current set (admin-only emergency recovery)
  const handleResetCurrentSet = async () => {
    if (!matchToReset) return;
    setResetting(true);
    try {
      await resetCurrentSet({
        matchId: matchToReset.id,
        reason: resetReason || undefined,
      });
      setShowResetConfirm(false);
      setMatchToReset(null);
      setResetReason('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset current set');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    setDeleting('bulk');
    try {
      for (const id of selected) await deleteMatchHard(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkConfirm = async () => {
    if (bulkAction === 'all' && confirmText !== 'DELETE ALL') return;
    setDeleting('bulk-action');
    try {
      if (bulkAction === 'all') {
        await deleteAllMatchesHard();
      } else if (bulkAction === 'completed') {
        await deleteMatchesByStatus('match-won');
      } else if (bulkAction === 'discarded') {
        await deleteMatchesByStatus('discarded');
      }
      setBulkAction(null);
      setConfirmText('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setDeleting(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(m => m.id)));
    }
  };

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      live: 'Live', 'set-won': 'Set Won', 'match-won': 'Completed',
      discarded: 'Discarded', setup: 'Setup', 'first-server': 'Pending',
    };
    return map[s] ?? s;
  };

  const statusColors: Record<string, string> = {
    live: 'bg-warning/20 text-warning',
    'set-won': 'bg-warning/20 text-warning',
    'match-won': 'bg-success/20 text-success',
    discarded: 'bg-error/20 text-error',
    setup: 'bg-surface-raised text-text-muted',
    'first-server': 'bg-surface-raised text-text-muted',
  };

  const filtered = filterStatus === 'all' ? matches : matches.filter(m => {
    if (filterStatus === 'live') return m.status === 'live' || m.status === 'set-won';
    if (filterStatus === 'pending') return m.status === 'setup' || m.status === 'first-server';
    return m.status === filterStatus;
  });

  const statCards = [
    { label: 'Total', value: stats.total, color: 'text-text-primary' },
    { label: 'Live', value: stats.live, color: 'text-warning' },
    { label: 'Completed', value: stats.completed, color: 'text-success' },
    { label: 'Discarded', value: stats.discarded, color: 'text-error' },
    { label: 'Pending', value: stats.pending, color: 'text-text-muted' },
  ];

  return (
    <>
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader onSignOut={onSignOut} title="Match Management" navIcon="admin" onNavigate={() => navigate('/admin')} />

      <div className="flex-1 overflow-y-auto px-4 py-5 safe-area-bottom max-w-lg mx-auto w-full">
        {error && (
          <div className="bg-error/15 border border-error/30 rounded-lg p-3 mb-4 flex items-center justify-between">
            <p className="text-error text-sm">{error}</p>
            <button onClick={() => setError(null)} className="text-error text-lg font-bold ml-2">×</button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-5 gap-2 mb-5">
          {statCards.map(s => (
            <div key={s.label} className="card text-center py-3">
              <p className={`text-lg font-bold ${s.color}`}>{loading ? '—' : s.value}</p>
              <p className="text-[10px] text-text-muted uppercase tracking-wide mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Orphan Matches Warning */}
        {orphans.length > 0 && (
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full bg-warning animate-pulse" />
              <h3 className="text-sm font-bold text-warning uppercase tracking-wider">Orphan Matches Found</h3>
            </div>
            <p className="text-xs text-text-secondary mb-3">
              {orphans.length} active match{orphans.length > 1 ? 'es' : ''} without a tournament. These are invisible in Tournament Dashboard.
            </p>
            <button
              onClick={() => setShowOrphanModal(true)}
              className="w-full py-2.5 px-4 rounded-lg text-sm font-bold bg-warning/20 text-warning border border-warning/40 hover:bg-warning/30 transition-colors active:scale-[0.99]"
            >
              Review & Repair ({orphans.length})
            </button>
          </div>
        )}

        {/* Bulk Actions */}
        <div className="card mb-5">
          <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-3">Bulk Actions</p>
          <div className="grid grid-cols-1 gap-2">
            <button
              onClick={() => { setBulkAction('completed'); setConfirmText(''); }}
              disabled={stats.completed === 0 || !!deleting}
              className="py-2.5 px-4 rounded-lg text-sm font-semibold bg-surface-raised border border-border text-text-primary disabled:opacity-40 hover:border-success/50 transition-colors active:scale-[0.99]"
            >
              Delete All Completed ({stats.completed})
            </button>
            <button
              onClick={() => { setBulkAction('discarded'); setConfirmText(''); }}
              disabled={stats.discarded === 0 || !!deleting}
              className="py-2.5 px-4 rounded-lg text-sm font-semibold bg-surface-raised border border-border text-text-primary disabled:opacity-40 hover:border-warning/50 transition-colors active:scale-[0.99]"
            >
              Delete All Discarded ({stats.discarded})
            </button>
            <button
              onClick={() => { setBulkAction('all'); setConfirmText(''); }}
              disabled={stats.total === 0 || !!deleting}
              className="py-2.5 px-4 rounded-lg text-sm font-bold bg-error/10 border border-error/30 text-error disabled:opacity-40 hover:bg-error/20 transition-colors active:scale-[0.99]"
            >
              Delete ALL Matches ({stats.total})
            </button>
            {selected.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                disabled={!!deleting}
                className="py-2.5 px-4 rounded-lg text-sm font-bold bg-error/10 border border-error/30 text-error disabled:opacity-40 hover:bg-error/20 transition-colors active:scale-[0.99] flex items-center justify-center gap-2"
              >
                {deleting === 'bulk' ? (
                  <>
                    <span className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : `Delete Selected (${selected.size})`}
              </button>
            )}
          </div>
        </div>

        {/* Filter + List */}
        <div className="flex items-center gap-2 mb-3">
          <p className="text-xs font-bold text-text-muted uppercase tracking-wider flex-1">Matches</p>
          <select
            value={filterStatus}
            onChange={e => { setFilterStatus(e.target.value); setSelected(new Set()); }}
            className="text-xs bg-surface border border-border rounded-lg px-2 py-1 text-text-primary"
          >
            <option value="all">All ({stats.total})</option>
            <option value="live">Live ({stats.live})</option>
            <option value="match-won">Completed ({stats.completed})</option>
            <option value="discarded">Discarded ({stats.discarded})</option>
            <option value="pending">Pending ({stats.pending})</option>
          </select>
          {filtered.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="text-xs text-text-muted underline"
            >
              {selected.size === filtered.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card text-center py-10 border-dashed">
            <p className="text-text-muted text-sm">No matches in this category.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(m => {
              const isLive = m.status === 'live' || m.status === 'set-won';
              const isCompleted = m.status === 'match-won';
              const isDiscarded = m.status === 'discarded';
              const canResume = (isLive || m.status === 'first-server') && !isDiscarded;
              const canDiscard = isAdmin && !isDiscarded && !isCompleted;

              return (
                <div
                  key={m.id}
                  className={`card transition-colors ${selected.has(m.id) ? 'border-error/50 bg-error/5' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selected.has(m.id)}
                      onChange={() => toggleSelect(m.id)}
                      className="w-4 h-4 flex-shrink-0 accent-error mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold text-text-primary">#{m.matchNumber}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyBroadcastUrl(m.broadcastId); }}
                          className="flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-info/10 text-info hover:bg-info/20 transition-colors"
                          title={`Click to copy overlay URL`}
                        >
                          <span>{m.broadcastId}</span>
                          {copiedId === m.broadcastId ? (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          )}
                        </button>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${statusColors[m.status] ?? 'bg-surface-raised text-text-muted'}`}>
                          {statusLabel(m.status)}
                        </span>
                      </div>
                      {m.tournamentName && (
                        <div className="flex items-center gap-1.5 mt-1 mb-0.5">
                          <span className="text-[10px] text-text-secondary">🏆</span>
                          <span className="text-[10px] text-text-secondary font-medium truncate">{m.tournamentName}</span>
                        </div>
                      )}
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {m.teamAName} <span className="text-text-muted font-normal text-xs">vs</span> {m.teamBName}
                      </p>
                      <p className="text-[10px] text-text-muted mt-0.5">
                        {m.date ?? new Date(m.createdAt).toLocaleDateString()}
                        {m.matchType ? ` · ${m.matchType}` : ''}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                    <button
                      onClick={() => navigate(`/match/${m.id}/resume`)}
                      className="flex-1 py-2 px-3 rounded-lg text-xs font-semibold bg-info/10 text-info border border-info/30 hover:bg-info/20 transition-colors"
                    >
                      View
                    </button>
                    {canResume && (
                      <button
                        onClick={() => navigate(`/match/${m.id}/resume`)}
                        className="flex-1 py-2 px-3 rounded-lg text-xs font-bold bg-success/10 text-success border border-success/30 hover:bg-success/20 transition-colors"
                      >
                        Resume
                      </button>
                    )}
                    {isLive && isAdmin && (
                      <button
                        onClick={() => { setMatchToReset(m); setShowResetConfirm(true); setResetReason(''); }}
                        disabled={resetting}
                        className="py-2 px-3 rounded-lg text-xs font-semibold bg-warning/10 text-warning border border-warning/30 hover:bg-warning/20 transition-colors disabled:opacity-50"
                        title="Emergency reset of current set"
                      >
                        Reset Set
                      </button>
                    )}
                    {canDiscard && (
                      <button
                        onClick={() => setDiscardConfirmId(m.id)}
                        disabled={discarding}
                        className="py-2 px-3 rounded-lg text-xs font-semibold bg-error/10 text-error border border-error/30 hover:bg-error/20 transition-colors disabled:opacity-50"
                      >
                        Discard
                      </button>
                    )}
                    {isDiscarded && isAdmin && (
                      <button
                        onClick={() => handleDeleteOne(m.id)}
                        disabled={!!deleting}
                        className="py-2 px-3 rounded-lg text-xs font-semibold text-error hover:bg-error/10 transition-colors disabled:opacity-40"
                        title="Delete permanently"
                      >
                        {deleting === m.id ? (
                          <span className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin block" />
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4h6v2" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bulk Action Confirmation Modal */}
      {bulkAction && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            {bulkAction === 'all' ? (
              <>
                <h3 className="text-lg font-bold text-text-primary mb-1">Delete ALL Matches?</h3>
                <p className="text-sm text-text-secondary mb-4">
                  This will permanently delete all {stats.total} matches, including all sets and rally data. This cannot be undone.
                </p>
                <p className="text-sm font-semibold text-text-primary mb-2">
                  Type <span className="text-error font-bold">DELETE ALL</span> to confirm:
                </p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="DELETE ALL"
                  className="input-field mb-4 text-center font-bold tracking-widest"
                  autoFocus
                />
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-text-primary mb-1">
                  Delete All {bulkAction === 'completed' ? 'Completed' : 'Discarded'} Matches?
                </h3>
                <p className="text-sm text-text-secondary mb-6">
                  This will permanently delete {bulkAction === 'completed' ? stats.completed : stats.discarded} matches and all their data. This cannot be undone.
                </p>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setBulkAction(null); setConfirmText(''); }}
                disabled={!!deleting}
                className="py-3 px-4 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border transition-all active:scale-[0.98] disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                onClick={handleBulkConfirm}
                disabled={!!deleting || (bulkAction === 'all' && confirmText !== 'DELETE ALL')}
                className="py-3 px-4 rounded-lg font-bold text-sm bg-error text-white transition-all active:scale-[0.98] disabled:opacity-40"
              >
                {deleting === 'bulk-action' ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    DELETING...
                  </span>
                ) : 'CONFIRM DELETE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discard Match Confirmation Modal */}
      {discardConfirmId && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-text-primary mb-1">Discard Match?</h3>
            <p className="text-sm text-text-secondary mb-4">
              This will mark the match as discarded. The match will be hidden from Live Matches but preserved for audit history. This can be undone by an admin.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setDiscardConfirmId(null)}
                disabled={discarding}
                className="py-3 px-4 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border transition-all active:scale-[0.98] disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                onClick={() => handleDiscardMatch(discardConfirmId)}
                disabled={discarding}
                className="py-3 px-4 rounded-lg font-bold text-sm bg-error text-white transition-all active:scale-[0.98] disabled:opacity-40"
              >
                {discarding ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    DISCARDING...
                  </span>
                ) : 'DISCARD MATCH'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orphan Match Repair Modal */}
      {showOrphanModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-text-primary mb-1">Orphan Matches</h3>
            <p className="text-sm text-text-secondary mb-4">
              These matches have no tournament assigned. Assign them to a tournament or discard them.
            </p>

            {orphans.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-success font-semibold">No orphan matches found!</p>
                <button
                  onClick={() => setShowOrphanModal(false)}
                  className="mt-4 btn-secondary px-6 py-2"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {orphans.map(o => {
                  const teamA = teams.find(t => t.id === o.team_a_id);
                  const teamB = teams.find(t => t.id === o.team_b_id);
                  const isSelected = selectedOrphan?.id === o.id;

                  return (
                    <div
                      key={o.id}
                      className={`rounded-lg p-3 border ${
                        isSelected
                          ? 'bg-warning/10 border-warning/40'
                          : 'bg-surface-raised border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-text-muted">#{o.match_number}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${statusColors[o.status] ?? 'bg-surface-raised text-text-muted'}`}>
                          {statusLabel(o.status)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        {teamA && (
                          <div className="flex items-center gap-1">
                            {teamA.logoUrl ? (
                              <img src={teamA.logoUrl} alt={teamA.shortName} className="w-4 h-4 rounded object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: teamA.color }} />
                            )}
                            <span className="text-sm font-semibold" style={{ color: teamA.color }}>{teamA.shortName}</span>
                          </div>
                        )}
                        <span className="text-xs text-text-muted">vs</span>
                        {teamB && (
                          <div className="flex items-center gap-1">
                            {teamB.logoUrl ? (
                              <img src={teamB.logoUrl} alt={teamB.shortName} className="w-4 h-4 rounded object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: teamB.color }} />
                            )}
                            <span className="text-sm font-semibold" style={{ color: teamB.color }}>{teamB.shortName}</span>
                          </div>
                        )}
                      </div>

                      {isSelected ? (
                        <div className="mt-3 pt-3 border-t border-border">
                          <label className="block text-xs text-text-muted mb-1">Assign to Tournament:</label>
                          <select
                            value={assignTournamentId}
                            onChange={e => setAssignTournamentId(e.target.value)}
                            className="input-field text-sm mb-3"
                          >
                            <option value="">Select Tournament...</option>
                            {tournaments.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                          <div className="grid grid-cols-3 gap-2">
                            <button
                              onClick={() => { setSelectedOrphan(null); setAssignTournamentId(''); }}
                              disabled={repairing}
                              className="py-2 px-3 rounded-lg text-xs font-semibold bg-surface-raised border border-border text-text-primary disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleDiscardOrphan}
                              disabled={repairing}
                              className="py-2 px-3 rounded-lg text-xs font-bold bg-error/10 text-error border border-error/30 disabled:opacity-50"
                            >
                              {repairing ? '...' : 'Discard'}
                            </button>
                            <button
                              onClick={handleAssignOrphan}
                              disabled={repairing || !assignTournamentId}
                              className="py-2 px-3 rounded-lg text-xs font-bold bg-success text-white disabled:opacity-50"
                            >
                              {repairing ? '...' : 'Assign'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedOrphan(o)}
                          className="w-full py-2 text-xs font-semibold text-warning hover:bg-warning/10 rounded transition-colors"
                        >
                          Repair this match
                        </button>
                      )}
                    </div>
                  );
                })}

                <button
                  onClick={() => setShowOrphanModal(false)}
                  className="w-full mt-4 py-3 px-4 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Reset Current Set Confirmation Modal */}
    {showResetConfirm && matchToReset && (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
        <div className="bg-surface rounded-2xl shadow-2xl max-w-md w-full p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center">
              <span className="text-xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-white">RESET CURRENT SET</h2>
          </div>

          <div className="space-y-3 text-sm text-white/80 mb-5">
            <p>
              This will permanently remove all rallies, timeouts, rotation, statistics and scoring data
              for <strong className="text-white">the current set</strong> only.
            </p>
            <p>Completed sets will <strong className="text-white">NOT</strong> be affected.</p>
            <p className="text-warning font-semibold">This action cannot be undone.</p>
          </div>

          <div className="mb-5">
            <label className="block text-xs font-semibold text-white/60 mb-2 uppercase tracking-wider">
              Reason (optional)
            </label>
            <textarea
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              placeholder="e.g. Wrong server selected, corrupted data..."
              className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-primary min-h-[80px] resize-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => { setShowResetConfirm(false); setMatchToReset(null); setResetReason(''); }}
              disabled={resetting}
              className="flex-1 py-3 rounded-xl font-bold text-sm bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleResetCurrentSet}
              disabled={resetting}
              className="flex-1 py-3 rounded-xl font-bold text-sm bg-warning text-black hover:opacity-90 transition-colors disabled:opacity-50"
            >
              {resetting ? 'Resetting...' : 'Reset Current Set'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
